import React, { useState } from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import ExportDialog from './ExportDialog';
import Export from '../services/Export';
import ReducersIndex from 'reduxModules/ducks';
import RdButton from '../atoms/RdButton';
import ThemeToggle from '../atoms/ThemeToggle';
import { set } from 'lodash';
import { showError } from 'utilities/toastManager';
import { useAuth } from '../../contexts/AuthContext';

interface BottomPanelProps {
  selectedCount: number;
  onExport: () => void;
  onExportPDF: () => void;
  onImport: () => void;
  onDelete: () => void;
  onSignout: () => void;
  onNewDesign: () => void;
  onShare?: () => void;
  organizationId?: string;
}

const BottomPanel: React.FC<BottomPanelProps> = ({
  selectedCount,
  onExport,
  onExportPDF,
  onImport,
  onDelete,
  onNewDesign,
  onShare,
  organizationId
}) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const handleMyOrganization = () => {
    // No fallback URL intentional — a stage fallback would silently redirect production users to the wrong environment.
    const url = new URL(`${process.env.REACT_APP_WORKPLACE_HOME_URL}/external/organizations`);
    url.searchParams.set('redirect', window.location.origin);
    window.location.href = url.toString();
  };

  // Get organizationId from sessionStorage if not provided as prop
  const currentOrganizationId = organizationId || sessionStorage.getItem('selectedOrgId') || '';

  const handleExportClick = () => {
    setExportDialogOpen(true);
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
  };

  const handleExportWorkplace = () => {
    // Use the same pattern as DesignContainer - call the original handler for bulk operations
    onExport();
    setExportDialogOpen(false);
  };

  const handleExportPDF = () => {
    try {
      onExportPDF();
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showError('Failed to export designs as PDF. Please try again.');
    } 
  };

  const handleNewDesignClick = () => {
    // Just navigate to conference room designer
    // The designer will decide whether to show the dialog
    onNewDesign();
  };


  return (
    <PanelContainer>
      {/* Left side - My Organizations button, Theme toggle, and selected count */}
      <LeftSection>
        <Tooltip
          title="My organizations"
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: (theme) => theme.palette.background.paper,
                color: (theme) => theme.palette.text.primary,
                fontSize: '12px',
                fontFamily: 'Open Sans',
                fontWeight: 600,
                padding: '8px 12px',
                '& .MuiTooltip-arrow': {
                  color: (theme) => theme.palette.background.paper,
                },
              },
            },
          }}
        >
          <OrganizationsButton onClick={handleMyOrganization}>
            <Box
              component="img"
              src="/images/OrgsIcon.svg"
              alt="My organizations"
              sx={{
                width: '20px',
                height: '20px',
                filter: (theme) => theme.palette.mode === 'dark'
                  ? 'brightness(0) invert(1)'
                  : 'none'
              }}
            />
          </OrganizationsButton>
        </Tooltip>
        <ThemeToggle size="medium" />
        {selectedCount > 0 && (
          <SelectedCountText>
            {selectedCount} design{selectedCount > 1 ? 's' : ''} selected
          </SelectedCountText>
        )}
      </LeftSection>

      {/* Right side - Action buttons */}
      <RightSection>
        {/* Share button - only visible when a Share handler is provided and designs are selected */}
        {onShare && selectedCount > 0 && (
          <RdButton
            onClick={onShare}
            sx={{
              '& img': {
                filter: (theme) => theme.palette.mode === 'dark'
                  ? 'brightness(0) invert(1)'
                  : 'none',
              },
            }}
            startIcon={
              <img
                src="/images/Share.svg"
                alt="Share"
                style={{ width: '16px', height: '16px' }}
              />
            }
          >
            Share
          </RdButton>
        )}
        
        {/* Import button is always visible */}
        <RdButton
          onClick={onImport}
          sx={{
            '& img': {
              filter: (theme) => theme.palette.mode === 'dark'
                ? 'brightness(0) invert(1)'
                : 'none',
            },
          }}
          startIcon={
            <img
              src="/images/Import.svg"
              alt="Import"
              style={{ width: '16px', height: '16px' }}
            />
          }
        >
          Import
        </RdButton>
        
        {/* Export button - only visible when designs are selected */}
        {selectedCount > 0 && (
          <RdButton
            onClick={handleExportClick}
            sx={{
              '& img': {
                filter: (theme) => theme.palette.mode === 'dark'
                  ? 'brightness(0) invert(1)'
                  : 'none',
              },
            }}
            startIcon={
              <img
                src="/images/Export.svg"
                alt="Export"
                style={{ width: '16px', height: '16px' }}
              />
            }
          >
            Export
          </RdButton>
        )}
        
        {/* Delete button - only visible when designs are selected */}
        {selectedCount > 0 && (
          <RdButton
            onClick={onDelete}
            sx={{
              backgroundColor: (theme) => `${theme.palette.error.main} !important`,
              color: (theme) => `${theme.palette.error.contrastText || 'white'} !important`,
              '&:hover': {
                backgroundColor: (theme) => `${theme.palette.error.dark} !important`,
              },
              '& img': {
                filter: 'brightness(0) invert(1)',
              },
            }}
            startIcon={
              <img
                src="/images/Delete.svg"
                alt="Delete"
                style={{ width: '16px', height: '16px' }}
              />
            }
          >
            Delete
          </RdButton>
        )}
        
        {/* New button is always visible */}
        <RdButton
          buttonType="primary"
          onClick={handleNewDesignClick}
          startIcon={<AddIcon sx={{ width: '16px', height: '16px' }} />}
        >
          Create design
        </RdButton>
      </RightSection>  
      <ExportDialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        onExportWorkplace={handleExportWorkplace}
        onExportPDF={handleExportPDF}
      />
    </PanelContainer>
  );
};

// Styled Components
const PanelContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  height: '60px',
  background: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 40px',
  zIndex: 1000,
}));

const LeftSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const SelectedCountText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontFamily: 'Open Sans',
  fontWeight: 600,
  color: theme.palette.text.primary,
}));


const OrganizationsButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  width: '44px',
  height: '44px',
  padding: '0',
  borderRadius: '6px',
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.default,
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    filter: theme.palette.mode === 'dark' 
      ? 'brightness(0) invert(1)' 
      : 'none',
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const RightSection = styled(Box)({
  display: 'flex',
  gap: '15px',
});

export default BottomPanel;